import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { IAdminKnmKind } from '../../../APIPackage/AdminModule/interfaces/IAdminKnmKind';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getCatalogKnmKindsAll } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';

type KnmKindForm = Omit<IAdminKnmKind, 'id'>;

interface IKnmKindEditFormProps {
  onBack: () => void;
}

interface IKnmKindEditFormStoreProps {
  knmKinds: IAdminKnmKind[];
}

const DISABLED_DECISION_MESSAGE_PART_1 = 'В соответствии ';
const DISABLED_DECISION_MESSAGE_PART_2 = ' решением является карточка паспорта ЕРКНМ';

const knmKindEditFormWithoutStore = ({ onBack, knmKinds }: IKnmKindEditFormProps & IKnmKindEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmKind, setSelectedKnmKind] = useState<IAdminKnmKind | undefined>();
  const [isFormOpen, setIsFormOpen] = useState(false);

  /**
   * Обработчик выделения вида КНМ в списке
   */
  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmKind = knmKinds.find(k => k?.dictionaryRecordId?.toString() === id);

      if (knmKind) {
        let message = knmKind.disabledDecisionMessage?.replace(DISABLED_DECISION_MESSAGE_PART_1, '');

        message = message?.replace(DISABLED_DECISION_MESSAGE_PART_2, '');

        setSelectedKnmKind(prev =>
          prev?.dictionaryRecordId === knmKind?.dictionaryRecordId
            ? undefined
            : { ...knmKind, disabledDecisionMessage: message },
        );
      }
    },
    [knmKinds],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_KINDS_ALL));
  }, [dispatch]);

  /**
   * Обработчик открытия формы редактирования вида КНМ
   */
  const onEdit = useCallback(() => {
    setIsFormOpen(true);
  }, []);

  /**
   * Обработчик выхода из формы редактирования вида КНМ
   */
  const onCancel = useCallback(() => {
    setSelectedKnmKind(undefined);
    setIsFormOpen(false);
  }, []);

  /**
   * Обработчик сохранения формы редактирования вида КНМ
   */
  const onSubmit = useCallback(
    (values: KnmKindForm) => {
      let disabledDecisionMessage = '';

      if (!values.decisionDisabled) {
        disabledDecisionMessage = '';
      } else if (values.disabledDecisionMessage) {
        disabledDecisionMessage = `${DISABLED_DECISION_MESSAGE_PART_1}${values.disabledDecisionMessage}${DISABLED_DECISION_MESSAGE_PART_2}`;
      }

      if (values.decisionDisabled && !disabledDecisionMessage) {
        dispatch(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADMIN_KIND_KNM,
            renderContent: `Укажите сообщение для отключенного решения`,
          }),
        );

        return;
      }

      dispatch(
        adminUpdateCatalogTypes({
          data: { ...values, dictionaryRecordId: selectedKnmKind!.dictionaryRecordId, disabledDecisionMessage },
          catalogName: CommonCatalogsNamesEnum.KNM_KINDS_ALL,
          callback: () => {
            dispatch(
              addNoticeAction({
                noticeType: NoticeTypesEnum.SUCCESS,
                messageType: MessageTypesEnum.ADMIN_KIND_KNM,
                renderContent: `Изменения успешно применены`,
              }),
            );
          },
        }),
      );
      onCancel();
    },
    [dispatch, onCancel, selectedKnmKind],
  );

  /**
   * Отрисовка формы редактирования вида КНМ
   */
  const renderForm = useCallback(
    ({ handleSubmit, values }: FormRenderProps<KnmKindForm>): JSX.Element => {
      return (
        <form onSubmit={handleSubmit}>
          <Row>
            <FieldBlock label="Название" color="black">
              <TextInputField name="title" disabled={!!values.guid} validate={makeRequired('Поле обязательно')} />
            </FieldBlock>
          </Row>
          <Row>
            <CheckboxField type="checkbox" id="decisionDisabled" name="decisionDisabled" label="Решение отключено" />
          </Row>
          {values?.decisionDisabled && (
            <Row>
              <FieldBlock label="Сообщение для отключенного решения" color="black">
                <div className={styles.DisabledDecisionMessageBlock}>
                  <span>{DISABLED_DECISION_MESSAGE_PART_1}</span>
                  <TextInputField
                    validate={makeRequired('Укажите сообщение для отключенного решения')}
                    name="disabledDecisionMessage"
                  />
                  <span>{DISABLED_DECISION_MESSAGE_PART_2}</span>
                </div>
              </FieldBlock>
            </Row>
          )}
          <ModalActions placement="left" padding="none">
            <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
            <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
          </ModalActions>
        </form>
      );
    },
    [onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmKindForm> onSubmit={onSubmit} initialValues={selectedKnmKind} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Виды КНМ</span>
        {knmKinds &&
          knmKinds.map((knmKind: IAdminKnmKind) => (
            <button
              key={knmKind.dictionaryRecordId}
              className={
                selectedKnmKind?.dictionaryRecordId === knmKind.dictionaryRecordId
                  ? styles.DataRowSelected
                  : styles.DataRow
              }
              data-id={knmKind.dictionaryRecordId}
              onClick={onRowClick}
              type="button"
            >
              {knmKind.title}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        {selectedKnmKind && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmKindEditFormStoreProps => ({
  knmKinds: getCatalogKnmKindsAll(state),
});

export const KnmKindEditForm = connect(mapStateToProps)(knmKindEditFormWithoutStore);
